import {
  ComponentType, DetailedHTMLProps, FocusEvent, InputHTMLAttributes, KeyboardEvent, MouseEvent, MouseEventHandler,
  RefAttributes, forwardRef, useCallback,
} from 'react';

import { BackgroundProps, Size, SpacingProps, UIProps } from '../../theme/mixins';
import { DateTimeFormat } from '../../config';
import SC from './SC';

export type HtmlInputProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export interface InputProps extends BackgroundProps, SpacingProps {
  size?: Size;
  placeholder?: boolean;
  color?: string;
}

export interface DatePickerProps extends SC<UIProps> {
  selected: string;
  internalFormat?: DateTimeFormat;
  startDate?: string;
  endDate?: string;
  minDate?: string;
  maxDate?: string;
  minTime?: string;
  maxTime?: string;
  placeholder?: string;
  disabled?: boolean;
  dateFormat?: string | string[];
  timeFormat?: string;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  timeIntervals?: number;
  id?: string;
  onChange: (value: string | null, event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => any;
  onBlur?: (event: FocusEvent) => void;
}

export interface PhoneNumberInputProps extends SC<UIProps> {
  value: string;
  country: string;
  onChange: (result: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  name?: string;
  id?: string;
}

export const ENTER_KEY = 13;
export const BACKSPACE_KEY = 8;

interface WithScrollRestorationProps {
  resetScrollPosition?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

type WithScrollRestorationComponent<P extends WithScrollRestorationProps> = (
  ComponentType<P & RefAttributes<HTMLAnchorElement>>
);

export const withScrollRestoration = <P extends WithScrollRestorationProps>(
  Component: WithScrollRestorationComponent<P>,
  displayName: string,
) => {
  const Wrapped = forwardRef<HTMLAnchorElement, P & { resetScrollPosition?: boolean }>(
    ({ resetScrollPosition = true, onClick, ...props }, ref) => {
      const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
        // Don't scroll if user is holding Ctrl/Cmd
        if (resetScrollPosition && !event.ctrlKey && !event.metaKey) {
          window.scrollTo(0, 0);
        }

        onClick?.(event);
      }, [resetScrollPosition, onClick]);

      return (
        <Component
          ref={ref}
          onClick={handleClick}
          {...(props as any)}
        />
      );
    },
  );

  Wrapped.displayName = displayName;

  return Wrapped;
};
